import React, { useMemo } from "react";

import { Box, Button, CircularProgress, Divider, Typography } from "@mui/material";

import { useAuth } from "@/shared/stores/useAuth";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useNavigate, useParams } from "react-router-dom";

import { CardSerialNumber } from "@/modules/limited-edition/CardSerialNumber";
import DeckCardlimitedEditionFrontFaceupdated from "@/modules/limited-edition/DeckCardlimitedEditionFrontFaceupdated";
import {
  useVerifyPurchase,
  VerifyPurchaseData,
  VerifyPurchaseUser,
} from "@/modules/limited-edition/api/limitedEditionCard.api";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const ORANGE = "#ff8a00";
const ORANGE_BRIGHT = "#ff9d00";

const PAGE_BG = "#050505";
const PANEL_BG_2 = "#0d0e10";

const BORDER = "rgba(255,255,255,0.14)";

const MUTED = "rgba(255,255,255,0.58)";

const SOFT = "rgba(255,255,255,0.72)";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const formatDate = (value?: string | null) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}.${month}.${day}`;
};

const formatTime = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Seoul",
  }).format(date);
};

const getOwnerName = (user: VerifyPurchaseUser) => {
  const firstName = user.first_name?.trim() ?? "";

  const lastName = user.last_name?.trim() ?? "";

  const invalidLastName = /^-\d+$/.test(lastName);

  const fullName = [firstName, invalidLastName ? "" : lastName].filter(Boolean).join(" ").trim();

  return fullName || user.username || `User #${user.id}`;
};

const getCardTypeLabel = (type?: string | null) => {
  const normalized = String(type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const labels: Record<string, string> = {
    single_card: "SINGLE ALBUM CARD",
    season_card: "SEASON CARD",
    demo_card: "DEMO CARD",
  };

  return labels[normalized] ?? normalized;
};

/* -------------------------------------------------------------------------- */
/* Detail Row                                                                 */
/* -------------------------------------------------------------------------- */

type DetailRowProps = {
  label: string;

  children: React.ReactNode;

  last?: boolean;

  orange?: boolean;

  rightContent?: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({
  label,
  children,
  last = false,
  orange = false,
  rightContent,
}) => {
  return (
    <Box
      sx={{
        py: {
          xs: 1.05,
          sm: 1.15,
          md: 1.35,
        },

        borderBottom: last ? "none" : `1px solid ${BORDER}`,
      }}
    >
      <Typography
        sx={{
          mb: 0.28,

          fontSize: {
            xs: "0.68rem",
            sm: "0.72rem",
            md: "0.82rem",
          },

          color: MUTED,

          lineHeight: 1.35,

          letterSpacing: "0.01em",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          justifyContent: "space-between",

          gap: 1.25,
        }}
      >
        <Typography
          sx={{
            color: orange ? ORANGE : "#fff",

            fontSize: {
              xs: "0.86rem",
              sm: "0.9rem",
              md: "0.98rem",
            },

            lineHeight: 1.4,

            fontWeight: orange ? 500 : 400,

            wordBreak: "break-word",
          }}
        >
          {children}
        </Typography>

        {rightContent}
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Timeline Item                                                              */
/* -------------------------------------------------------------------------- */

type TimelineItemProps = {
  type: "issuer" | "owner" | "empty";

  title: string;

  subtitle?: string;

  last?: boolean;
};

const TimelineItem: React.FC<TimelineItemProps> = ({ type, title, subtitle, last = false }) => {
  const isIssuer = type === "issuer";

  const isEmpty = type === "empty";

  return (
    <Box
      sx={{
        position: "relative",

        display: "grid",

        gridTemplateColumns: {
          xs: "28px 1fr",

          sm: "32px 1fr",

          md: "38px 1fr",
        },

        columnGap: {
          xs: 1,
          sm: 1.15,
          md: 1.5,
        },

        minHeight: {
          xs: 64,
          sm: 70,
          md: 80,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",

          display: "flex",

          justifyContent: "center",
        }}
      >
        {!last && (
          <Box
            sx={{
              position: "absolute",

              top: 18,

              bottom: -8,

              width: "2px",

              background: isIssuer
                ? `linear-gradient(
                      180deg,
                      ${ORANGE_BRIGHT},
                      ${ORANGE} 58%,
                      rgba(255,138,0,0.16)
                    )`
                : `linear-gradient(
                      180deg,
                      ${ORANGE},
                      rgba(255,138,0,0.08)
                    )`,

              boxShadow: isIssuer ? "0 0 9px rgba(255,138,0,.45)" : "none",
            }}
          />
        )}

        <Box
          sx={{
            position: "relative",

            zIndex: 2,

            mt: 0.45,

            width: {
              xs: 15,
              md: 18,
            },

            height: {
              xs: 15,
              md: 18,
            },

            borderRadius: "50%",

            border: isEmpty
              ? "2px dashed rgba(255,255,255,.38)"
              : `2px solid ${isIssuer ? "#ffd83d" : ORANGE}`,

            bgcolor: PANEL_BG_2,

            boxShadow: isEmpty ? "none" : "0 0 8px rgba(255,138,0,.42)",
          }}
        />
      </Box>

      <Box
        sx={{
          pb: {
            xs: 1.2,
            md: 1.75,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",

            alignItems: "center",

            gap: {
              xs: 1,
              md: 1.25,
            },
          }}
        >
          {!isEmpty && (
            <Box
              sx={{
                width: {
                  xs: 30,
                  sm: 32,
                  md: 36,
                },

                height: {
                  xs: 30,
                  sm: 32,
                  md: 36,
                },

                borderRadius: "50%",

                border: isIssuer
                  ? "1px solid rgba(255,138,0,.72)"
                  : "1px solid rgba(255,255,255,.32)",

                display: "grid",

                placeItems: "center",

                color: isIssuer ? ORANGE : "#fff",

                flexShrink: 0,
              }}
            >
              {isIssuer ? (
                <Typography
                  sx={{
                    color: ORANGE,

                    fontSize: {
                      xs: "0.92rem",
                      md: "1.05rem",
                    },

                    lineHeight: 1,

                    fontWeight: 900,
                  }}
                >
                  ✱
                </Typography>
              ) : (
                <PersonOutlineRoundedIcon
                  sx={{
                    fontSize: {
                      xs: 18,
                      md: 20,
                    },
                  }}
                />
              )}
            </Box>
          )}

          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                color: "#fff",

                fontSize: {
                  xs: "0.82rem",

                  sm: "0.88rem",

                  md: "0.94rem",
                },

                fontWeight: 500,

                lineHeight: 1.35,

                wordBreak: "break-word",
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                sx={{
                  mt: 0.25,

                  color: "rgba(255,255,255,.55)",

                  fontSize: {
                    xs: "0.64rem",

                    sm: "0.68rem",

                    md: "0.74rem",
                  },

                  lineHeight: 1.45,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Verification Error                                                         */
/* -------------------------------------------------------------------------- */

type VerificationErrorProps = {
  message: string;
};

const VerificationError: React.FC<VerificationErrorProps> = ({ message }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",

        width: "100%",

        bgcolor: PAGE_BG,

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        justifyContent: "center",

        px: 2,

        textAlign: "center",

        color: "#fff",

        boxSizing: "border-box",
      }}
    >
      <VerifiedOutlinedIcon
        sx={{
          fontSize: {
            xs: 52,
            sm: 60,
            md: 70,
          },

          color: "rgba(255,255,255,.25)",
        }}
      />

      <Typography
        sx={{
          mt: 1.5,

          fontSize: {
            xs: "1.05rem",

            sm: "1.18rem",

            md: "1.3rem",
          },

          fontWeight: 600,
        }}
      >
        Unable to verify card
      </Typography>

      <Typography
        sx={{
          mt: 0.8,

          color: MUTED,

          maxWidth: 450,

          fontSize: {
            xs: "0.76rem",

            sm: "0.82rem",

            md: "0.9rem",
          },

          lineHeight: 1.55,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const LimitedEditionVerificationPage = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const isLoggedIn = Boolean(userInfo);
  const limitedEdition =
    new URLSearchParams(window.location.search).get("limitedEdition") === "true";

  /* ---------------------------------------------------------------------- */
  /* QR route params                                                        */
  /* ---------------------------------------------------------------------- */

  const { purchaseId } = useParams<{
    purchaseId: string;
  }>();

  /* ---------------------------------------------------------------------- */
  /* Validate purchase ID                                                   */
  /* ---------------------------------------------------------------------- */

  const numericPurchaseId = Number(purchaseId);

  const isValidPurchaseId =
    Boolean(purchaseId) && Number.isInteger(numericPurchaseId) && numericPurchaseId > 0;

  /* ---------------------------------------------------------------------- */
  /* Verification API Hook                                                  */
  /* ---------------------------------------------------------------------- */

  const {
    data: verificationResponse,

    isLoading,

    isError,

    error,
  } = useVerifyPurchase({
    key: ["verify-purchase", purchaseId],

    params: {
      purchaseId: purchaseId ?? "",
    },

    options: {
      enabled: isValidPurchaseId,
    },
  });

  /* ---------------------------------------------------------------------- */
  /* Backend response                                                       */
  /* ---------------------------------------------------------------------- */

  const data: VerifyPurchaseData | null = verificationResponse?.data ?? null;

  /* ---------------------------------------------------------------------- */
  /* Normalize API data                                                     */
  /* ---------------------------------------------------------------------- */

  const viewData = useMemo(() => {
    if (!data) {
      return null;
    }

    const ownerName = getOwnerName(data.user);

    const issuerName = data.talent.username;

    const cardType = getCardTypeLabel(data.main_card.type);

    const ownedEditionNumber = data.edition;

    const title = data.main_card.content?.title || "Limited Edition";

    const imageUrl = data.main_card.cover_image_url || data.main_card.content?.thumbnail || "";

    return {
      purchaseId: data.id,

      mainCardId: data.main_card.id,

      talentId: data.talent.id,

      ownerId: data.user.id,

      issuerName,
      limited_edition: data.main_card.limited_edition?.toString() ?? "0",

      ownerName,

      title,

      cardType,

      editionNumber: ownedEditionNumber,

      imageUrl,

      cardStatus: data.main_card.status,

      collectedDate: formatDate(data.created_at),

      collectedTime: formatTime(data.created_at),

      issuedDate: formatDate(data.main_card.created_at),

      issuedTime: formatTime(data.main_card.created_at),

      cardId:
        ownedEditionNumber != null
          ? `TAAS-${data.main_card.id}-${ownedEditionNumber}`
          : `TAAS-${data.main_card.id}`,
    };
  }, [data]);

  /* ---------------------------------------------------------------------- */
  /* Invalid URL                                                            */
  /* ---------------------------------------------------------------------- */

  if (!isValidPurchaseId) {
    return <VerificationError message="The purchase ID in this verification URL is invalid." />;
  }

  /* ---------------------------------------------------------------------- */
  /* Loading                                                                */
  /* ---------------------------------------------------------------------- */

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",

          width: "100%",

          bgcolor: PAGE_BG,

          display: "flex",

          flexDirection: "column",

          alignItems: "center",

          justifyContent: "center",

          gap: 1.5,

          color: "#fff",

          boxSizing: "border-box",
        }}
      >
        <CircularProgress
          size={30}
          sx={{
            color: ORANGE,
          }}
        />

        <Typography
          sx={{
            color: MUTED,

            fontSize: {
              xs: "0.76rem",

              md: "0.86rem",
            },
          }}
        >
          Verifying authentic TAAS card…
        </Typography>
      </Box>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* API Error                                                              */
  /* ---------------------------------------------------------------------- */

  if (isError || !data || !viewData) {
    const apiErrorMessage =
      error instanceof Error ? error.message : "Verification data could not be loaded.";

    return <VerificationError message={apiErrorMessage} />;
  }

  /* ---------------------------------------------------------------------- */
  /* Response validation                                                    */
  /* ---------------------------------------------------------------------- */

  const responseMatchesPurchase = Number(data.id) === numericPurchaseId;

  if (!responseMatchesPurchase) {
    return (
      <VerificationError message="The returned purchase does not match this verification link." />
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Edition                                                                */
  /* ---------------------------------------------------------------------- */

  const formattedEdition =
    viewData.editionNumber != null ? String(viewData.editionNumber).padStart(3, "0") : null;

  /* ---------------------------------------------------------------------- */
  /* Navigation                                                             */
  /* ---------------------------------------------------------------------- */

  const handleOpenCard = () => {
    const mainCardId = encodeURIComponent(String(viewData.mainCardId));

    const destination = isLoggedIn ? `/lobby?id=${mainCardId}` : `/lobby?id=${mainCardId}`;

    navigate(destination, {
      state: {
        openCardId: viewData.mainCardId,

        purchaseId: viewData.purchaseId,

        editionNumber: viewData.editionNumber,

        initialFace: "front",

        isLimitedEdition: true,
      },
    });
  };

  const handleEnterHall = () => {
    navigate(`/talent-hall/${viewData.talentId}`);
  };

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <Box
      sx={{
        minHeight: "100vh",

        width: "100%",

        bgcolor: PAGE_BG,

        color: "#fff",

        py: {
          xs: 1.5,

          sm: 3,

          md: 4.5,
        },

        px: {
          xs: 1,

          sm: 2,

          md: 2.5,
        },

        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",

          maxWidth: "900px",

          mx: "auto",
        }}
      >
        {/* ================================================================ */}
        {/* Authentic Header                                                */}
        {/* ================================================================ */}

        <Box
          sx={{
            position: "relative",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            gap: {
              xs: 1.25,
              sm: 2,
              md: 3,
            },

            px: {
              xs: 1.5,
              sm: 2.5,
              md: 4,
            },

            py: {
              xs: 1.6,
              sm: 2,
              md: 2.8,
            },

            borderRadius: {
              xs: "18px",

              sm: "22px",

              md: "30px",
            },

            background: `
                radial-gradient(
                  circle at 25% 50%,
                  rgba(255,138,0,.065),
                  transparent 24%
                ),
                linear-gradient(
                  145deg,
                  #111214 0%,
                  #0a0b0c 50%,
                  #111214 100%
                )
              `,

            border: "1px solid rgba(255,138,0,.64)",

            boxShadow: `
                inset 0 0 0 1px rgba(255,255,255,.07),
                0 10px 40px rgba(0,0,0,.7),
                0 0 20px rgba(255,138,0,.07)
              `,

            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",

              top: -1,

              left: "20%",

              width: "42%",

              height: "2px",

              background: `linear-gradient(
                  90deg,
                  transparent,
                  ${ORANGE},
                  #fff7ae,
                  ${ORANGE},
                  transparent
                )`,

              boxShadow: "0 0 12px rgba(255,138,0,.9)",
            }}
          />

          <Box
            sx={{
              width: {
                xs: 54,

                sm: 64,

                md: 88,
              },

              height: {
                xs: 54,

                sm: 64,

                md: 88,
              },

              display: "grid",

              placeItems: "center",

              color: ORANGE,

              flexShrink: 0,

              filter: "drop-shadow(0 0 9px rgba(255,138,0,.6))",
            }}
          >
            <VerifiedOutlinedIcon
              sx={{
                fontSize: {
                  xs: 50,

                  sm: 60,

                  md: 84,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                color: "#f5f5f5",

                fontSize: {
                  xs: "1.12rem",

                  sm: "1.45rem",

                  md: "1.8rem",
                },

                fontWeight: 700,

                lineHeight: 1.15,

                letterSpacing: "-0.02em",
              }}
            >
              Authentic TAAS Card
            </Typography>

            <Box
              sx={{
                mt: {
                  xs: 0.65,

                  md: 1,
                },

                display: "flex",

                alignItems: "center",

                gap: 0.7,
              }}
            >
              <Box
                sx={{
                  width: {
                    xs: 5,
                    md: 7,
                  },

                  height: {
                    xs: 5,
                    md: 7,
                  },

                  borderRadius: "50%",

                  bgcolor: ORANGE,

                  boxShadow: "0 0 9px rgba(255,138,0,.9)",

                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  color: MUTED,

                  fontSize: {
                    xs: "0.66rem",

                    sm: "0.74rem",

                    md: "0.84rem",
                  },

                  lineHeight: 1.4,
                }}
              >
                Live ownership verification
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ================================================================ */}
        {/* Main Verification Panel                                         */}
        {/* ================================================================ */}

        <Box
          sx={{
            mt: {
              xs: 1.5,

              sm: 2.25,

              md: 3,
            },

            px: {
              xs: 1.15,

              sm: 2,

              md: 3,
            },

            pt: {
              xs: 1.75,

              sm: 2.25,

              md: 3,
            },

            pb: {
              xs: 2,

              sm: 2.75,

              md: 3.5,
            },

            borderRadius: {
              xs: "18px",

              sm: "22px",

              md: "30px",
            },

            background: `
                linear-gradient(
                  155deg,
                  #151618 0%,
                  #101113 48%,
                  #0c0d0f 100%
                )
              `,

            border: "1px solid rgba(255,255,255,.15)",

            boxShadow: `
                inset 0 1px 0 rgba(255,255,255,.035),
                0 20px 70px rgba(0,0,0,.55)
              `,
          }}
        >
          {/* ============================================================ */}
          {/* Card + Metadata                                             */}
          {/* ============================================================ */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                md: "minmax(270px, 320px) minmax(0, 1fr)",
              },

              gap: {
                xs: 2.25,

                sm: 2.75,

                md: 3.5,
              },

              alignItems: "start",
            }}
          >
            <Box
              sx={{
                width: "100%",

                maxWidth: {
                  xs: "250px",

                  sm: "275px",

                  md: "300px",
                },

                justifySelf: "center",
              }}
            >
              <DeckCardlimitedEditionFrontFaceupdated
                compact
                totalEdition={viewData.limited_edition}
                talentName={viewData.issuerName}
                title={viewData.title}
                imageUrl={viewData.imageUrl}
                editionNumber={viewData.editionNumber}
                purchaseId={viewData.purchaseId}
                cardType={viewData.cardType.toUpperCase()}
              />
            </Box>

            {/* Metadata */}

            <Box
              sx={{
                minWidth: 0,

                pt: {
                  xs: 0,

                  md: 0.75,
                },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",

                  fontSize: {
                    xs: "1.08rem",

                    sm: "1.2rem",

                    md: "1.45rem",
                  },

                  fontWeight: 600,

                  lineHeight: 1.3,

                  mb: {
                    xs: 1.15,

                    md: 1.5,
                  },

                  wordBreak: "break-word",
                }}
              >
                {viewData.title}
              </Typography>

              <Divider
                sx={{
                  borderColor: BORDER,
                }}
              />
              {!!limitedEdition && (
                <Box
                  sx={{
                    py: {
                      xs: 1.2,
                      sm: 1.35,
                      md: 1.5,
                    },

                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <CardSerialNumber
                    cardInfo={{
                      mainCardId: viewData.mainCardId,
                      // talentId: viewData.talentId,
                      // purchaseId: viewData.purchaseId,
                      // ownerId: viewData.ownerId,
                      editionNumber: viewData.editionNumber,
                      // title: viewData.title,
                      // cardType: viewData.cardType,
                    }}
                  />
                </Box>
              )}

              <DetailRow label="Issuer">{viewData.issuerName}</DetailRow>

              <DetailRow label="Card Type">{viewData.cardType}</DetailRow>

              <DetailRow label="Main Card ID">#{viewData.mainCardId}</DetailRow>

              {formattedEdition && <DetailRow label="Edition">#{formattedEdition}</DetailRow>}

              <DetailRow
                label="Status"
                orange
                rightContent={
                  <VerifiedOutlinedIcon
                    sx={{
                      color: ORANGE,

                      fontSize: {
                        xs: 19,

                        md: 22,
                      },
                    }}
                  />
                }
              >
                Collected · Verified
              </DetailRow>

              <DetailRow label="Current Owner">{viewData.ownerName}</DetailRow>

              <DetailRow label="Collected Date" last>
                {viewData.collectedDate}
              </DetailRow>
            </Box>
          </Box>

          {/* ============================================================ */}
          {/* Ownership History                                           */}
          {/* ============================================================ */}

          {!!limitedEdition && (
            <Box
              sx={{
                mt: {
                  xs: 2.25,

                  sm: 2.75,

                  md: 3.5,
                },

                px: {
                  xs: 1.4,

                  sm: 1.75,

                  md: 3,
                },

                py: {
                  xs: 1.6,

                  sm: 2,

                  md: 2.8,
                },

                borderRadius: {
                  xs: "14px",

                  md: "16px",
                },

                bgcolor: "rgba(0,0,0,.16)",

                border: `1px solid ${BORDER}`,

                boxShadow: "inset 0 0 24px rgba(0,0,0,.22)",
              }}
            >
              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  gap: {
                    xs: 0.9,

                    md: 1.25,
                  },

                  mb: {
                    xs: 1.75,

                    md: 2.5,
                  },
                }}
              >
                <DescriptionOutlinedIcon
                  sx={{
                    color: ORANGE,

                    fontSize: {
                      xs: 20,

                      md: 23,
                    },
                  }}
                />

                <Typography
                  sx={{
                    color: "#fff",

                    fontSize: {
                      xs: "0.9rem",

                      sm: "0.95rem",

                      md: "1.08rem",
                    },

                    fontWeight: 600,
                  }}
                >
                  Ownership History
                </Typography>
              </Box>

              <TimelineItem
                type="issuer"
                title={`Issued by ${viewData.issuerName}`}
                subtitle={`${viewData.issuedDate}${
                  viewData.issuedTime ? `  ${viewData.issuedTime} (KST)` : ""
                }`}
              />

              <TimelineItem
                type="owner"
                title={`Collected by ${viewData.ownerName}`}
                subtitle={`${viewData.collectedDate}${
                  viewData.collectedTime ? `  ${viewData.collectedTime} (KST)` : ""
                }`}
              />

              <TimelineItem
                type="empty"
                title="No transfer recorded yet"
                subtitle="This card is currently held by this owner."
                last
              />
            </Box>
          )}

          {/* ============================================================ */}
          {/* Actions                                                     */}
          {/* ============================================================ */}

          <Box
            sx={{
              mt: {
                xs: 2.25,

                md: 3,
              },

              display: "flex",

              flexDirection: "column",

              gap: {
                xs: 1,

                md: 1.4,
              },
            }}
          >
            <Button
              fullWidth
              onClick={handleOpenCard}
              endIcon={
                <ChevronRightRoundedIcon
                  sx={{
                    fontSize: {
                      xs: "20px !important",

                      md: "24px !important",
                    },
                  }}
                />
              }
              sx={{
                minHeight: {
                  xs: 48,

                  sm: 52,

                  md: 58,
                },

                borderRadius: {
                  xs: "12px",

                  md: "14px",
                },

                fontSize: {
                  xs: "0.88rem",

                  sm: "0.94rem",

                  md: "1.05rem",
                },

                fontWeight: 500,

                textTransform: "none",

                color: "#fff",

                background: `linear-gradient(
                    105deg,
                    #ff9700 0%,
                    #ff7a00 52%,
                    #e75d00 100%
                  )`,

                border: "1px solid rgba(255,190,60,.9)",

                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,.2),
                    0 10px 24px rgba(255,105,0,.08)
                  `,

                transition: "transform 180ms ease, background 180ms ease",

                "&:hover": {
                  background: `linear-gradient(
                      105deg,
                      #ffa20c 0%,
                      #ff8200 52%,
                      #ee6500 100%
                    )`,

                  transform: "translateY(-1px)",
                },

                "&:active": {
                  transform: "translateY(0) scale(.99)",
                },
              }}
            >
              Open Card
            </Button>

            <Button
              fullWidth
              onClick={handleEnterHall}
              startIcon={
                <AccountBalanceOutlinedIcon
                  sx={{
                    fontSize: {
                      xs: "21px !important",

                      sm: "24px !important",

                      md: "27px !important",
                    },

                    color: "rgba(255,255,255,.55)",
                  }}
                />
              }
              endIcon={
                <ChevronRightRoundedIcon
                  sx={{
                    fontSize: {
                      xs: "20px !important",

                      md: "24px !important",
                    },
                  }}
                />
              }
              sx={{
                position: "relative",

                minHeight: {
                  xs: 48,

                  sm: 52,

                  md: 58,
                },

                borderRadius: {
                  xs: "12px",

                  md: "14px",
                },

                px: {
                  xs: 1.6,

                  md: 2,
                },

                fontSize: {
                  xs: "0.84rem",

                  sm: "0.9rem",

                  md: "1rem",
                },

                fontWeight: 400,

                textTransform: "none",

                color: "#fff",

                bgcolor: "rgba(5,5,5,.35)",

                border: `1px solid ${ORANGE}`,

                "& .MuiButton-endIcon": {
                  position: "absolute",

                  right: {
                    xs: 12,

                    md: 20,
                  },
                },

                "&:hover": {
                  bgcolor: "rgba(255,138,0,.06)",
                },
              }}
            >
              Enter {viewData.issuerName} Hall
            </Button>
          </Box>

          {/* ============================================================ */}
          {/* Verification Notice                                         */}
          {/* ============================================================ */}

          <Box
            sx={{
              mt: {
                xs: 1.5,

                md: 2,
              },

              px: {
                xs: 1.4,

                sm: 1.8,

                md: 2.4,
              },

              py: {
                xs: 1.3,

                sm: 1.5,

                md: 1.8,
              },

              borderRadius: {
                xs: "13px",

                md: "15px",
              },

              display: "flex",

              alignItems: "center",

              gap: {
                xs: 1,

                md: 1.5,
              },

              background: `
                  linear-gradient(
                    145deg,
                    rgba(255,255,255,.045),
                    rgba(255,255,255,.015)
                  )
                `,

              border: "1px solid rgba(255,255,255,.16)",
            }}
          >
            <SecurityOutlinedIcon
              sx={{
                flexShrink: 0,

                color: "rgba(255,255,255,.55)",

                fontSize: {
                  xs: 28,

                  sm: 32,

                  md: 38,
                },
              }}
            />

            <Typography
              sx={{
                color: SOFT,

                fontSize: {
                  xs: "0.7rem",

                  sm: "0.75rem",

                  md: "0.84rem",
                },

                lineHeight: 1.55,
              }}
            >
              This verification page is linked to purchase #{viewData.purchaseId}.
              <br />
              Ownership and edition information are fetched directly from TAAS.
            </Typography>
          </Box>

          {/* ============================================================ */}
          {/* Footer                                                       */}
          {/* ============================================================ */}

          <Box
            sx={{
              mt: {
                xs: 2.5,

                sm: 3,

                md: 4,
              },

              pb: 0.25,

              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: "#f5f5f5",

                fontSize: {
                  xs: "1.4rem",

                  sm: "1.55rem",

                  md: "1.9rem",
                },

                fontWeight: 700,

                letterSpacing: {
                  xs: "0.12em",

                  md: "0.14em",
                },

                lineHeight: 1,
              }}
            >
              TAAS
            </Typography>

            <Typography
              sx={{
                mt: 0.55,

                color: "rgba(255,255,255,.42)",

                fontSize: {
                  xs: "0.42rem",

                  sm: "0.46rem",

                  md: "0.52rem",
                },

                fontWeight: 500,

                letterSpacing: {
                  xs: "0.2em",

                  md: "0.24em",
                },
              }}
            >
              TRUE ARTISTS, AUTHENTIC STORIES
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LimitedEditionVerificationPage;
